import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { usePlatform } from "@/contexts/PlatformContext";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { toast } from "sonner";
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_AUTH_START_URL = `${API}/auth/google/start`;
const GOOGLE_AUTH_RETURN_PATH_KEY = "lotq_google_auth_return_path";
const GOOGLE_AUTH_APP_REDIRECT = "lordofthequiz://auth";

export function AuthModal({ open, onClose, defaultTab = "login" }) {
  const [tab, setTab] = useState(defaultTab);
  const [view, setView] = useState("main"); // main | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, register } = useAuth();
  const { isApp } = usePlatform();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (tab === "login") {
        await login(email, password);
        toast.success("Login efetuado com sucesso!");
      } else {
        await register(name, email, password);
        toast.success("Conta criada com sucesso!");
      }
      onClose();
      resetForm();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao processar. Tente novamente.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Digite seu email."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("Se o email existir, enviaremos instruções de recuperação.");
      setView("main");
    } catch {
      toast.error("Erro ao processar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (!process.env.REACT_APP_BACKEND_URL) {
      toast.error("Login com Google indisponível no momento.");
      return;
    }
    const returnPath = `${window.location.pathname}${window.location.search || ""}`;
    window.localStorage.setItem(GOOGLE_AUTH_RETURN_PATH_KEY, returnPath);

    if (isApp && Capacitor.isNativePlatform()) {
      const googleAuthUrl = `${GOOGLE_AUTH_START_URL}?redirect=${encodeURIComponent(GOOGLE_AUTH_APP_REDIRECT)}`;
      try {
        // Keep the WebView alive so Capacitor can receive the OAuth deep link.
        await Browser.open({ url: googleAuthUrl });
      } catch (error) {
        console.error("Unable to open native Google sign-in:", error);
        window.localStorage.removeItem(GOOGLE_AUTH_RETURN_PATH_KEY);
        toast.error("Não foi possível abrir o login com Google. Tente novamente.");
      }
      return;
    }

    const redirectUrl = window.location.origin;
    window.location.href = `${GOOGLE_AUTH_START_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setShowPassword(false);
    setView("main");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); resetForm(); } }}>
      <DialogContent className="bg-zinc-950 border border-white/10 max-w-md p-0 max-h-[90dvh] overflow-y-auto" data-testid="auth-modal" aria-describedby="auth-modal-desc">
        <DialogTitle className="sr-only">Autenticação</DialogTitle>
        <p id="auth-modal-desc" className="sr-only">Faça login ou crie uma conta para acessar o Quiz Club</p>

        {/* Forgot Password View */}
        {view === "forgot" ? (
          <div className="p-4 sm:p-6 space-y-5">
            <button
              onClick={() => setView("main")}
              className="flex items-center gap-2 font-outfit text-sm text-zinc-500 hover:text-[#fccf1f] transition-colors"
              data-testid="forgot-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Voltar ao login
            </button>
            <div>
              <h3 className="font-nunito font-bold text-xl text-white">Esqueceu sua senha?</h3>
              <p className="font-outfit text-sm text-zinc-400 mt-1">
                Digite seu email e enviaremos instruções para redefinir sua senha.
              </p>
            </div>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="pl-10 bg-zinc-900/80 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#fccf1f] h-12"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[#fccf1f] text-black font-nunito font-bold uppercase tracking-wide hover:bg-[#e6bc1c] transition-all"
                data-testid="forgot-submit-btn"
              >
                {submitting ? "Enviando..." : "Enviar Instruções"}
              </Button>
            </form>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex border-b border-white/10">
              <button
                onClick={() => { setTab("login"); resetForm(); }}
                className={`flex-1 py-4 font-nunito font-bold text-sm uppercase tracking-wider transition-colors ${
                  tab === "login" ? "text-[#fccf1f] border-b-2 border-[#fccf1f]" : "text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid="auth-tab-login"
              >
                Entrar
              </button>
              <button
                onClick={() => { setTab("register"); resetForm(); }}
                className={`flex-1 py-4 font-nunito font-bold text-sm uppercase tracking-wider transition-colors ${
                  tab === "register" ? "text-[#fccf1f] border-b-2 border-[#fccf1f]" : "text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid="auth-tab-register"
              >
                Criar Conta
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              {/* Google Login */}
              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full h-12 border-white/20 text-white hover:bg-white/5 font-outfit gap-3"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuar com Google
              </Button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-zinc-500 font-outfit uppercase tracking-wider">ou</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Email Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {tab === "register" && (
                  <div className="space-y-2">
                    <Label className="text-zinc-400 font-outfit text-sm">Nome</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Seu nome"
                        required
                        minLength={2}
                        className="pl-10 bg-zinc-900/80 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#fccf1f] h-12"
                        data-testid="auth-name-input"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-zinc-400 font-outfit text-sm">{tab === "login" ? "Email ou Username" : "Email"}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type={tab === "register" ? "email" : "text"}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={tab === "login" ? "email ou username" : "seu@email.com"}
                      required
                      className="pl-10 bg-zinc-900/80 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#fccf1f] h-12"
                      data-testid="auth-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400 font-outfit text-sm">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                      minLength={6}
                      className="pl-10 pr-10 bg-zinc-900/80 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#fccf1f] h-12"
                      data-testid="auth-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {tab === "login" && (
                  <button
                    type="button"
                    onClick={() => setView("forgot")}
                    className="font-outfit text-xs text-zinc-500 hover:text-[#fccf1f] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Esqueceu sua senha?
                  </button>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[#fccf1f] text-black font-nunito font-bold uppercase tracking-wide hover:bg-[#e6bc1c] hover:scale-[1.02] transition-all shadow-[0_0_20px_rgba(252,207,31,0.2)]"
                  data-testid="auth-submit-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      {tab === "login" ? "Entrando..." : "Criando conta..."}
                    </span>
                  ) : (
                    tab === "login" ? "Entrar" : "Criar Conta"
                  )}
                </Button>
              </form>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
